import { PopoverInteractionKind, Position } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import React from 'react'
import { size } from 'lodash'
import cloneDeep from 'lodash/cloneDeep'
import { IUserSettings } from '@api'
import { Icon, Flex } from '@ui'
import forEachNode, { LocationTreeNodeT } from '../forEachNode'
import { AddFavoriteButtonStyled, MarkDefaultLocationButtonStyled } from '../../../components'
import css from './renderTreeContents.module.scss'

export enum LocationNodeType {
  Location = 'Location',
  City = 'City',
  Building = 'Building',
  Floor = 'Floor',
}

type RenderNode<SettingsT> = (node: LocationTreeNodeT, settings: SettingsT) => void
type NodeComponents = { [key in LocationNodeType]: RenderNode<SettingsLocationArgs> }

const renderNodeLocation: RenderNode<SettingsLocationArgs> = (node) => {
  const locationName = node.label

  node.label = (
    <div className={css.line_text}>
      {locationName} <hr className="line" />
    </div>
  )
}

const renderNodeCity: RenderNode<SettingsLocationArgs> = (node) => {
  const cityName = node.label as string
  node.icon = <Icon name="city" />
  node.label = (
    <div className={css.render_tree_title}>
      {cityName}, {node?.nodeData?.city?.state || ''} <span> </span> ({size(node.childNodes)})
    </div>
  )
}

const renderNodeBuilding: RenderNode<SettingsLocationArgs> = (node) => {
  const buildingName = node.label as string
  const building = node.nodeData?.building

  node.className = 'building-segment'
  node.icon = building?.isBuildingHidden ? <Icon name="eye-slash" /> : <Icon name="building" />
  node.label = (
    <div className={css.building}>
      <div className={css.render_tree_title}>
        {buildingName} <span> </span> ({size(node.childNodes)})
      </div>
      <Tooltip2
        content={
          <>
            <div>{buildingName}</div>
            {building?.address}
          </>
        }
        position={Position.BOTTOM}
        interactionKind={PopoverInteractionKind.HOVER_TARGET_ONLY}>
        <div className={`${css.address} truncate`}>{building?.address}</div>
      </Tooltip2>
    </div>
  )
}

const renderNodeFloor: RenderNode<SettingsLocationArgs> = (node, settings) => {
  const defaultFloor = settings.userSettings?.defaultFloor || null
  const isDefault = defaultFloor === node.id

  const favoriteFloors = settings.userSettings?.favoriteFloors || []
  const isFavorite = favoriteFloors.includes(node.id as string)
  const isHidden = node.nodeData?.floor?.hidden || false
  const couldAddFavorite = size(favoriteFloors) < 10
  const floorName = node.label

  node.icon = isHidden ? <Icon name="eye-slash" /> : <Icon name="map-marked-alt" />
  node.label = (
    <Flex align="baseline">
      <div className={css.render_tree_title}>{floorName}</div>
      <div className="ml-auto">
        <AddFavoriteButtonStyled
          isFavorite={isFavorite}
          couldAddFavorite={couldAddFavorite}
          onClick={(e) => {
            e.stopPropagation()
            settings?.handleToggleFavorite(node.id as string, isDefault)
          }}
        />
      </div>
    </Flex>
  )
}

const renderFavoritesNodeFloor: RenderNode<SettingsFavoritesArgs> = (node, settings) => {
  const building = node.nodeData?.floor?.building
  const locationName = node.label
  const address = `${building?.name || ''}, ${building?.city || ''}, ${building?.address || ''}`
  const defaultFloor = settings.userSettings?.defaultFloor || null
  const nodeId = node.id as string
  const isDefault = defaultFloor === node.id
  const isHidden = node.nodeData?.floor?.hidden || false

  node.className = 'building-segment'
  node.icon = isHidden ? <Icon name="eye-slash" /> : <Icon name="map-marked-alt" />
  node.label = (
    <div className={css.favorite}>
      <div className={css.render_tree_title}>
        <Flex align="baseline">
          {locationName}
          <div className="ml-auto">
            <MarkDefaultLocationButtonStyled
              isDefault={isDefault}
              onClick={(e) => {
                e.stopPropagation()
                settings?.handleToggleDefaultLocation(isDefault ? null : nodeId)
              }}
            />
            <AddFavoriteButtonStyled
              isFavorite={true}
              onClick={(e) => {
                e.stopPropagation()
                settings?.handleRemoveFavorite(nodeId, isDefault)
              }}
            />
          </div>
        </Flex>
      </div>
      <Tooltip2
        content={
          <>
            <div>{locationName}</div>
            {address}
          </>
        }
        position={Position.BOTTOM}
        interactionKind={PopoverInteractionKind.HOVER_TARGET_ONLY}>
        <div className={`${css.address} truncate`}>{address}</div>
      </Tooltip2>
    </div>
  )
}

const locationNodeComponents: NodeComponents = {
  [LocationNodeType.City]: renderNodeCity,
  [LocationNodeType.Building]: renderNodeBuilding,
  [LocationNodeType.Floor]: renderNodeFloor,
  [LocationNodeType.Location]: renderNodeLocation,
}

type SettingsLocationArgs = {
  userSettings: IUserSettings | undefined
  handleToggleFavorite: (favoriteFloorId: string, isDefault: boolean) => void
}

export const renderLocationTreeContents = (
  treeNodes: LocationTreeNodeT[],
  settings: SettingsLocationArgs
) => {
  // it's necessary to mutate the state
  const clonedTreeNodes = cloneDeep(treeNodes)

  forEachNode((node) => {
    if (node.nodeData) {
      locationNodeComponents[node.nodeData.nodeType]?.(node, settings)
    }
  }, clonedTreeNodes)

  return clonedTreeNodes
}

type SettingsFavoritesArgs = {
  userSettings: IUserSettings | undefined
  handleToggleDefaultLocation: (defaultFloorId: string | null) => void
  handleRemoveFavorite: (id: string, isDefault: boolean) => void
}

export const renderFavoritesTreeContents = (
  treeNodes: LocationTreeNodeT[],
  settings: SettingsFavoritesArgs
) => {
  // it's necessary to mutate the state
  const clonedTreeNodes = cloneDeep(treeNodes)

  forEachNode((node) => {
    if (node?.nodeData?.nodeType === LocationNodeType.Floor) {
      renderFavoritesNodeFloor(node, settings)
    }
  }, clonedTreeNodes)

  return clonedTreeNodes
}
